import React, { useState, useEffect, useCallback } from 'react'
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Chip, IconButton, TextField,
  InputAdornment, Button, Tooltip, Stack, Alert, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider, Card,
  CardContent, Grid, Tab, Tabs, FormControl, InputLabel, Select, MenuItem,
} from '@mui/material'
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  OpenInNew as OpenInNewIcon,
  LocalShipping as ShippingIcon,
  CheckCircle as DeliveredIcon,
  HourglassEmpty as PendingIcon,
  DirectionsBus as InTransitIcon,
  Cancel as CancelledIcon,
  Label as LabelIcon,
  Sync as SyncIcon,
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import api from '../services/api'

interface ShipmentItem {
  id: string
  order_number: string
  awb_number: string
  carrier_name: string | null
  shipment_status: string | null
  tracking_url: string | null
  order_status: string | null
  customer_name: string | null
  total_amount: number
  shipping_address: { city: string | null; state: string | null; pincode: string | null } | null
  created_at: string | null
}

interface ShipmentsResponse {
  total: number
  page: number
  page_size: number
  pages: number
  shipments: ShipmentItem[]
}

const STATUS_CONFIG: Record<string, { label: string; color: 'default' | 'primary' | 'success' | 'error' | 'warning' | 'info'; icon: React.ReactNode }> = {
  created:          { label: 'Created',          color: 'info',    icon: <PendingIcon sx={{ fontSize: 14 }} /> },
  pickup_scheduled: { label: 'Pickup Scheduled', color: 'primary', icon: <ShippingIcon sx={{ fontSize: 14 }} /> },
  picked_up:        { label: 'Picked Up',        color: 'primary', icon: <ShippingIcon sx={{ fontSize: 14 }} /> },
  in_transit:       { label: 'In Transit',       color: 'warning', icon: <InTransitIcon sx={{ fontSize: 14 }} /> },
  out_for_delivery: { label: 'Out for Delivery', color: 'warning', icon: <InTransitIcon sx={{ fontSize: 14 }} /> },
  delivered:        { label: 'Delivered',        color: 'success', icon: <DeliveredIcon sx={{ fontSize: 14 }} /> },
  returned:         { label: 'Returned (RTO)',   color: 'error',   icon: <CancelledIcon sx={{ fontSize: 14 }} /> },
  cancelled:        { label: 'Cancelled',        color: 'error',   icon: <CancelledIcon sx={{ fontSize: 14 }} /> },
  failed:           { label: 'Failed',           color: 'error',   icon: <CancelledIcon sx={{ fontSize: 14 }} /> },
}

function StatusChip({ status }: { status: string | null }) {
  const cfg = status ? STATUS_CONFIG[status.toLowerCase()] : null
  if (!cfg) return <Chip label={status || 'Unknown'} size="small" />
  return (
    <Chip
      icon={cfg.icon as React.ReactElement}
      label={cfg.label}
      color={cfg.color}
      size="small"
      variant="outlined"
    />
  )
}

function CarrierChip({ carrier }: { carrier: string | null }) {
  if (!carrier) return <Chip label="Unknown" size="small" variant="outlined" />
  const isDelhivery = carrier.toLowerCase().includes('delhivery')
  const isShiprocket = carrier.toLowerCase().includes('shiprocket')
  return (
    <Chip
      label={carrier}
      size="small"
      sx={{
        bgcolor: isDelhivery ? '#e3f2fd' : isShiprocket ? '#fce4ec' : '#f5f5f5',
        color: isDelhivery ? '#1565c0' : isShiprocket ? '#c62828' : '#616161',
        fontWeight: 600,
        fontSize: 11,
        border: 'none',
      }}
    />
  )
}

// ------- Create Shipment Dialog -------

interface CreateDialogProps {
  open: boolean
  orderId: string
  orderNumber: string
  onClose: () => void
  onSuccess: () => void
}

function CreateShipmentDialog({ open, orderId, orderNumber, onClose, onSuccess }: CreateDialogProps) {
  const { enqueueSnackbar } = useSnackbar()
  const [provider, setProvider] = useState<'delhivery' | 'shiprocket'>('delhivery')
  const [warehouse, setWarehouse] = useState('NOIDA')
  const [pickupLocation, setPickupLocation] = useState('Primary')
  const [weight, setWeight] = useState('')
  const [length, setLength] = useState('30')
  const [width, setWidth] = useState('20')
  const [height, setHeight] = useState('15')
  const [loading, setLoading] = useState(false)

  const handleCreate = async () => {
    setLoading(true)
    try {
      const params: Record<string, string> = {}
      if (weight) params.weight = weight
      if (length) params.length = length
      if (width) params.width = width
      if (height) params.height = height

      if (provider === 'delhivery') {
        params.warehouse_name = warehouse
        await api.post(`/admin/shipments/sales-order/${orderId}/create`, null, { params })
      } else {
        params.pickup_location = pickupLocation || 'Primary'
        await api.post(`/admin/shipments/sales-order/${orderId}/create-shiprocket`, null, { params })
      }
      enqueueSnackbar(`Shipment created via ${provider === 'delhivery' ? 'Delhivery' : 'Shiprocket'}`, { variant: 'success' })
      onSuccess()
      onClose()
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.detail || 'Failed to create shipment', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Shipment — Order {orderNumber}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Carrier</InputLabel>
            <Select value={provider} label="Carrier" onChange={e => setProvider(e.target.value as any)}>
              <MenuItem value="delhivery">🚚 Delhivery (Direct Express)</MenuItem>
              <MenuItem value="shiprocket">🚀 Shiprocket (Aggregator)</MenuItem>
            </Select>
          </FormControl>

          {provider === 'delhivery' ? (
            <FormControl fullWidth size="small">
              <InputLabel>Warehouse</InputLabel>
              <Select value={warehouse} label="Warehouse" onChange={e => setWarehouse(e.target.value)}>
                <MenuItem value="NOIDA">NOIDA (Sector 62, Uttar Pradesh)</MenuItem>
                <MenuItem value="NIMIYA">NIMIYA (Daltonganj, Jharkhand)</MenuItem>
              </Select>
            </FormControl>
          ) : (
            <TextField
              label="Pickup Location Name"
              size="small"
              fullWidth
              value={pickupLocation}
              onChange={e => setPickupLocation(e.target.value)}
              helperText="Must match the pickup location name in your Shiprocket account (default: Primary)"
            />
          )}

          <TextField
            label="Weight (kg)"
            size="small"
            fullWidth
            type="number"
            value={weight}
            onChange={e => setWeight(e.target.value)}
            helperText="Leave blank to use calculated weight from items"
            inputProps={{ min: 0.1, step: 0.1 }}
          />

          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            Dimensions (cm)
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={4}>
              <TextField
                label="Length"
                size="small"
                fullWidth
                type="number"
                value={length}
                onChange={e => setLength(e.target.value)}
                inputProps={{ min: 1, step: 1 }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Width"
                size="small"
                fullWidth
                type="number"
                value={width}
                onChange={e => setWidth(e.target.value)}
                inputProps={{ min: 1, step: 1 }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Height"
                size="small"
                fullWidth
                type="number"
                value={height}
                onChange={e => setHeight(e.target.value)}
                inputProps={{ min: 1, step: 1 }}
              />
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : <ShippingIcon />}
        >
          {loading ? 'Creating…' : 'Create Shipment'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ------- Main Page -------

const CARRIER_TABS = [
  { label: 'All Carriers', value: '' },
  { label: 'Delhivery', value: 'delhivery' },
  { label: 'Shiprocket', value: 'shiprocket' },
]

export default function ShipmentsDashboard() {
  const { enqueueSnackbar } = useSnackbar()

  const [shipments, setShipments] = useState<ShipmentItem[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [carrierTab, setCarrierTab] = useState(0)
  const [loading, setLoading] = useState(false)

  const [trackDialog, setTrackDialog] = useState<{ open: boolean; order: ShipmentItem | null }>({ open: false, order: null })
  const [trackData, setTrackData] = useState<any>(null)
  const [trackLoading, setTrackLoading] = useState(false)

  const [createDialog, setCreateDialog] = useState<{ open: boolean; orderId: string; orderNumber: string }>({
    open: false, orderId: '', orderNumber: ''
  })

  // Direct AWB Tracking state
  const [directTrackOpen, setDirectTrackOpen] = useState(false)
  const [directAwb, setDirectAwb] = useState('')
  const [directCarrier, setDirectCarrier] = useState<'delhivery' | 'shiprocket'>('delhivery')
  const [directTrackResult, setDirectTrackResult] = useState<any>(null)
  const [directTrackLoading, setDirectTrackLoading] = useState(false)

  const handleDirectTrack = async () => {
    if (!directAwb.trim()) {
      enqueueSnackbar('Please enter an AWB number', { variant: 'warning' })
      return
    }
    setDirectTrackLoading(true)
    setDirectTrackResult(null)
    try {
      const res = await api.get('/admin/shipments/track-awb', {
        params: { awb_number: directAwb.trim(), carrier: directCarrier }
      })
      setDirectTrackResult(res.data)
      enqueueSnackbar('Tracking info retrieved', { variant: 'success' })
    } catch (err: any) {
      setDirectTrackResult({ error: err?.response?.data?.detail || 'Failed to track AWB' })
    } finally {
      setDirectTrackLoading(false)
    }
  }

  const carrierFilter = CARRIER_TABS[carrierTab].value

  const statusCounts: Record<string, number> = {}
  shipments.forEach(s => {
    const k = s.shipment_status || 'unknown'
    statusCounts[k] = (statusCounts[k] || 0) + 1
  })

  const fetchShipments = useCallback(async () => {
    setLoading(true)
    try {
      const params: any = { page: page + 1, page_size: rowsPerPage }
      if (search) params.search = search
      if (statusFilter) params.shipment_status_filter = statusFilter
      if (carrierFilter) params.carrier = carrierFilter
      const res = await api.get<ShipmentsResponse>('/admin/shipments/', { params })
      setShipments(res.data.shipments)
      setTotal(res.data.total)
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.detail || 'Failed to load shipments', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }, [page, rowsPerPage, search, statusFilter, carrierFilter, enqueueSnackbar])

  useEffect(() => { fetchShipments() }, [fetchShipments])

  const handleTrack = async (order: ShipmentItem) => {
    setTrackDialog({ open: true, order })
    setTrackData(null)
    setTrackLoading(true)
    try {
      const res = await api.get(`/admin/shipments/sales-order/${order.id}/track`)
      setTrackData(res.data)
    } catch (err: any) {
      setTrackData({ error: err?.response?.data?.detail || 'Tracking failed' })
    } finally {
      setTrackLoading(false)
    }
  }

  const handleGetLabel = async (order: ShipmentItem) => {
    try {
      const res = await api.get(`/admin/shipments/sales-order/${order.id}/label`)
      if (res.data.label_url) {
        window.open(res.data.label_url, '_blank', 'noopener,noreferrer')
      }
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.detail || 'Failed to get label', { variant: 'error' })
    }
  }

  const summaryStats = [
    { label: 'Total Shipments', value: total, color: '#1565c0' },
    { label: 'In Transit', value: (statusCounts['in_transit'] || 0) + (statusCounts['out_for_delivery'] || 0), color: '#f57c00' },
    { label: 'Delivered', value: statusCounts['delivered'] || 0, color: '#2e7d32' },
    { label: 'RTO / Failed', value: (statusCounts['returned'] || 0) + (statusCounts['failed'] || 0), color: '#c62828' },
  ]

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" gap={2} mb={3}>
        <Stack direction="row" alignItems="center" gap={1.5}>
          <ShippingIcon sx={{ fontSize: 32, color: '#1565c0' }} />
          <Box>
            <Typography variant="h5" fontWeight={700} color="#1a237e">
              Shipments &amp; Logistics
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Unified view — Delhivery &amp; Shiprocket (API &amp; Direct Portal Bookings)
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" gap={1.5}>
          <Button
            startIcon={<SearchIcon />}
            variant="contained"
            color="primary"
            size="small"
            onClick={() => setDirectTrackOpen(true)}
            sx={{ fontWeight: 600 }}
          >
            Track Direct / External AWB
          </Button>
          <Button startIcon={<RefreshIcon />} onClick={fetchShipments} variant="outlined" size="small" disabled={loading}>
            Refresh
          </Button>
        </Stack>
      </Stack>

      {/* Summary cards */}
      <Grid container spacing={2} mb={3}>
        {summaryStats.map(stat => (
          <Grid item xs={6} sm={3} key={stat.label}>
            <Card variant="outlined" sx={{ borderColor: `${stat.color}40` }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="h4" fontWeight={700} color={stat.color}>{stat.value}</Typography>
                <Typography variant="caption" color="text.secondary">{stat.label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Carrier tabs + filters */}
      <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} mb={2} alignItems={{ sm: 'center' }} flexWrap="wrap">
        <Tabs
          value={carrierTab}
          onChange={(_, v) => { setCarrierTab(v); setPage(0) }}
          sx={{ minWidth: 300 }}
        >
          {CARRIER_TABS.map((t, i) => <Tab key={i} label={t.label} />)}
        </Tabs>

        <TextField
          placeholder="Search order # or AWB…"
          size="small"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(0) }}
          sx={{ minWidth: 240 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
          }}
        />

        <TextField
          select
          SelectProps={{ native: true }}
          size="small"
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(0) }}
          sx={{ minWidth: 170 }}
          label="Status"
        >
          <option value="">All Statuses</option>
          {Object.entries(STATUS_CONFIG).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </TextField>
      </Stack>

      {/* Table */}
      <Paper variant="outlined">
        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={36} />
          </Box>
        )}
        {!loading && (
          <TableContainer>
            <Table size="small">
              <TableHead sx={{ bgcolor: '#f5f7fa' }}>
                <TableRow>
                  <TableCell><strong>Order #</strong></TableCell>
                  <TableCell><strong>AWB</strong></TableCell>
                  <TableCell><strong>Carrier</strong></TableCell>
                  <TableCell><strong>Customer</strong></TableCell>
                  <TableCell><strong>Destination</strong></TableCell>
                  <TableCell><strong>Amount</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell align="right"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {shipments.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                      No shipments found
                    </TableCell>
                  </TableRow>
                )}
                {shipments.map(row => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} color="#1565c0">
                        {row.order_number}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace" fontSize={12}>
                        {row.awb_number}
                      </Typography>
                    </TableCell>
                    <TableCell><CarrierChip carrier={row.carrier_name} /></TableCell>
                    <TableCell>{row.customer_name || '—'}</TableCell>
                    <TableCell>
                      {row.shipping_address
                        ? `${row.shipping_address.city || ''}, ${row.shipping_address.state || ''} ${row.shipping_address.pincode || ''}`
                        : '—'}
                    </TableCell>
                    <TableCell>₹{row.total_amount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</TableCell>
                    <TableCell><StatusChip status={row.shipment_status} /></TableCell>
                    <TableCell>
                      {row.created_at
                        ? new Date(row.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })
                        : '—'}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" justifyContent="flex-end" gap={0.5}>
                        <Tooltip title="Track Shipment">
                          <IconButton size="small" onClick={() => handleTrack(row)} color="primary">
                            <SyncIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download Label">
                          <IconButton size="small" onClick={() => handleGetLabel(row)}>
                            <LabelIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {row.tracking_url && (
                          <Tooltip title={`Track on ${row.carrier_name || 'carrier'}`}>
                            <IconButton
                              size="small"
                              component="a"
                              href={row.tracking_url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value)); setPage(0) }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      {/* Track dialog */}
      <Dialog open={trackDialog.open} onClose={() => setTrackDialog({ open: false, order: null })} maxWidth="sm" fullWidth>
        <DialogTitle>
          Tracking — {trackDialog.order?.awb_number}
          <Typography variant="caption" color="text.secondary" display="block">
            Order: {trackDialog.order?.order_number} · <CarrierChip carrier={trackDialog.order?.carrier_name ?? null} />
          </Typography>
        </DialogTitle>
        <DialogContent>
          {trackLoading && <CircularProgress size={28} sx={{ display: 'block', mx: 'auto', my: 2 }} />}
          {!trackLoading && trackData?.error && (
            <Alert severity="error">{trackData.error}</Alert>
          )}
          {!trackLoading && trackData && !trackData.error && (
            <Box>
              <Stack direction="row" gap={2} mb={2} flexWrap="wrap">
                <Box>
                  <Typography variant="caption" color="text.secondary">Current Status</Typography>
                  <Box mt={0.5}>
                    <StatusChip status={trackData.tracking_info?.status || trackData.tracking_info?.data?.status} />
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Location</Typography>
                  <Typography variant="body2">
                    {trackData.tracking_info?.current_location || trackData.tracking_info?.data?.current_location || '—'}
                  </Typography>
                </Box>
              </Stack>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" mb={1}>Tracking History</Typography>
              {(trackData.tracking_info?.tracking_history || trackData.tracking_info?.data?.tracking_history || []).length === 0 && (
                <Typography variant="body2" color="text.secondary">No tracking events yet.</Typography>
              )}
              {(trackData.tracking_info?.tracking_history || trackData.tracking_info?.data?.tracking_history || []).map((evt: any, i: number) => (
                <Box key={i} sx={{ mb: 1, pl: 2, borderLeft: '3px solid #e0e0e0' }}>
                  <Typography variant="caption" color="text.secondary">{evt.timestamp}</Typography>
                  <Typography variant="body2">{evt.description || evt.status}</Typography>
                  <Typography variant="caption" color="text.secondary">{evt.location}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {trackDialog.order?.tracking_url && (
            <Button
              component="a"
              href={trackDialog.order.tracking_url}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<OpenInNewIcon />}
              size="small"
            >
              {trackDialog.order?.carrier_name || 'Carrier'} Site
            </Button>
          )}
          <Button onClick={() => setTrackDialog({ open: false, order: null })}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Direct AWB Tracking Dialog */}
      <Dialog
        open={directTrackOpen}
        onClose={() => { setDirectTrackOpen(false); setDirectTrackResult(null); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" gap={1}>
            <ShippingIcon color="primary" />
            <Box>
              <Typography variant="h6" fontWeight={700}>Track Direct / External Booking</Typography>
              <Typography variant="caption" color="text.secondary">
                Track shipments booked directly on Delhivery or Shiprocket portals
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Carrier</InputLabel>
              <Select
                value={directCarrier}
                label="Carrier"
                onChange={e => setDirectCarrier(e.target.value as any)}
              >
                <MenuItem value="delhivery">🚚 Delhivery</MenuItem>
                <MenuItem value="shiprocket">🚀 Shiprocket</MenuItem>
              </Select>
            </FormControl>

            <Stack direction="row" spacing={1}>
              <TextField
                fullWidth
                size="small"
                label="AWB / Waybill Number"
                placeholder="e.g. 1284918294812"
                value={directAwb}
                onChange={e => setDirectAwb(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleDirectTrack() }}
              />
              <Button
                variant="contained"
                onClick={handleDirectTrack}
                disabled={directTrackLoading || !directAwb.trim()}
                sx={{ px: 3, textTransform: 'none', fontWeight: 600 }}
              >
                {directTrackLoading ? <CircularProgress size={20} color="inherit" /> : 'Track'}
              </Button>
            </Stack>

            {directTrackResult?.error && (
              <Alert severity="error">{directTrackResult.error}</Alert>
            )}

            {directTrackResult && !directTrackResult.error && (
              <Box sx={{ mt: 1, p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">AWB Number</Typography>
                    <Typography variant="subtitle1" fontWeight={700} fontFamily="monospace">
                      {directTrackResult.awb_number}
                    </Typography>
                  </Box>
                  <CarrierChip carrier={directTrackResult.carrier} />
                </Stack>

                <Stack direction="row" gap={3} mb={2} flexWrap="wrap">
                  <Box>
                    <Typography variant="caption" color="text.secondary">Status</Typography>
                    <Box mt={0.5}>
                      <StatusChip status={directTrackResult.tracking_info?.status || directTrackResult.tracking_info?.data?.status} />
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Current Location</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {directTrackResult.tracking_info?.current_location || directTrackResult.tracking_info?.data?.current_location || 'In Transit / Hub'}
                    </Typography>
                  </Box>
                  {directTrackResult.order_number && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Linked Order</Typography>
                      <Typography variant="body2" fontWeight={700} color="#1565c0">
                        {directTrackResult.order_number}
                      </Typography>
                    </Box>
                  )}
                </Stack>

                <Divider sx={{ my: 1.5 }} />

                <Typography variant="subtitle2" fontWeight={600} mb={1}>
                  Carrier Scan History
                </Typography>

                {(() => {
                  const history = directTrackResult.tracking_info?.tracking_history ||
                    directTrackResult.tracking_info?.data?.tracking_history || []
                  if (history.length === 0) {
                    return (
                      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                        Shipment registered with courier. Scans will appear once dispatched from hub.
                      </Typography>
                    )
                  }
                  return (
                    <Stack spacing={1} sx={{ maxHeight: 250, overflowY: 'auto', pr: 1 }}>
                      {history.map((evt: any, i: number) => (
                        <Box key={i} sx={{ pl: 1.5, borderLeft: '3px solid #1565c0', py: 0.5 }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {evt.timestamp || evt.date}
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {evt.description || evt.status || evt.activity}
                          </Typography>
                          {evt.location && (
                            <Typography variant="caption" color="text.secondary">
                              📍 {evt.location}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Stack>
                  )
                })()}
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDirectTrackOpen(false); setDirectTrackResult(null); }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create shipment dialog */}
      <CreateShipmentDialog
        open={createDialog.open}
        orderId={createDialog.orderId}
        orderNumber={createDialog.orderNumber}
        onClose={() => setCreateDialog({ open: false, orderId: '', orderNumber: '' })}
        onSuccess={fetchShipments}
      />
    </Box>
  )
}
